import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Code2, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/orgApi";
import { canWriteMainTab } from "@/lib/maintabAccess";
import { mrnOf } from "@/lib/patientApi";
import { PAT } from "@/constants/testIds";
import PatientForm from "@/components/patient/PatientForm";
import FhirViewer from "@/components/organization/FhirViewer";

const fullName = (r) => {
  const n = r.name?.[0];
  if (!n) return "—";
  return [...(n.given || []), n.family].filter(Boolean).join(" ") || "—";
};
const genderCap = (g) => (g ? g.charAt(0).toUpperCase() + g.slice(1) : "—");
const orgOf = (r) => r.managingOrganization?.display || "—";

export default function PatientsPage() {
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "patients");
  const [pts, setPts] = useState([]);
  const [demoIds, setDemoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fhirTarget, setFhirTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (name) => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params: name ? { name } : {} });
      setPts(data.items || []);
      setDemoIds(new Set(data.demoIds || []));
    } catch {
      toast.error("Failed to load patients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/patients/${deleteTarget.id}`);
      toast.success("Patient deleted.");
      setDeleteTarget(null);
      load(search.trim() || undefined);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid={PAT.page} className="emr-pat">
      <div className="emr-pat__pagehead">
        <div>
          <h1 data-testid={PAT.title} className="emr-pat__title">
            Patients
          </h1>
          <p className="emr-pat__subtitle">
            Individuals receiving care — FHIR <span className="font-fhir">Patient</span> resource
          </p>
        </div>
        {canWrite ? (
          <button
            type="button"
            data-testid={PAT.newBtn}
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="btn emr-pat__register"
          >
            <Plus className="emr-pat__register-icon" aria-hidden="true" />
            Register Patient
          </button>
        ) : null}
      </div>

      <div className="emr-pat__search">
        <Search className="emr-pat__search-icon" aria-hidden="true" />
        <Input
          data-testid={PAT.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="emr-pat__search-input"
          aria-label="Search patients by name"
        />
      </div>

      <section className="card emr-pat__panel">
        {loading ? (
          <div data-testid={PAT.loading} className="emr-pat__status">Loading…</div>
        ) : pts.length === 0 ? (
          <div data-testid={PAT.emptyState} className="emr-pat__empty">
            <Users className="emr-pat__empty-icon" aria-hidden="true" />
            <p>No patients yet. Register your first patient.</p>
          </div>
        ) : (
          <div className="emr-pat__table-wrap">
            <table data-testid={PAT.table} className="striped highlight emr-pat__table">
              <thead>
                <tr>
                  <th>MRN</th>
                  <th>Name</th>
                  <th>Gender</th>
                  <th>DOB</th>
                  <th>Organization</th>
                  <th className="emr-pat__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pts.map((r) => (
                  <tr key={r.id} data-testid={`${PAT.row}-${r.id}`}>
                    <td>
                      <Link to={`/patients/${r.id}`} data-testid={`${PAT.mrnLink}-${r.id}`} className="emr-pat__mrn">
                        {mrnOf(r)}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/patients/${r.id}`}
                        data-testid={`${PAT.nameLink}-${r.id}`}
                        className="emr-pat__name"
                      >
                        {fullName(r)}
                      </Link>
                      {demoIds.has(r.id) && (
                        <span data-testid={`demo-badge-${r.id}`} className="emr-pat__demo">DEMO</span>
                      )}
                    </td>
                    <td>{genderCap(r.gender)}</td>
                    <td className="font-fhir">{r.birthDate || "—"}</td>
                    <td>{orgOf(r)}</td>
                    <td>
                      <div className="emr-pat__row-actions">
                        <Button variant="ghost" size="icon" title="View FHIR" className="emr-pat-icon-btn"
                          data-testid={`${PAT.viewFhirBtn}-${r.id}`} onClick={() => setFhirTarget(r)}>
                          <Code2 className="h-4 w-4" />
                        </Button>
                        {canWrite ? (
                          <Button variant="ghost" size="icon" title="Edit" className="emr-pat-icon-btn"
                            data-testid={`${PAT.editBtn}-${r.id}`}
                            onClick={() => { setEditing(r); setFormOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {canWrite ? (
                          <Button variant="ghost" size="icon" title="Delete" className="emr-pat-icon-btn emr-pat-icon-btn--danger"
                            data-testid={`${PAT.deleteBtn}-${r.id}`} onClick={() => setDeleteTarget(r)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <PatientForm
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => load(search.trim() || undefined)}
        contentClassName="emr-pat-form"
      />

      <FhirViewer open={!!fhirTarget} resource={fhirTarget} onClose={() => setFhirTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={PAT.deleteDialog} className="emr-pat-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete patient?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-medium">{fullName(deleteTarget || {})}</span> (MRN {deleteTarget ? mrnOf(deleteTarget) : ""}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={PAT.confirmDelete} onClick={confirmDelete}
              className="emr-pat-dialog__delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
